import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  FileText, DownloadSimple, CalendarBlank, CurrencyDollar,
  CheckCircle, Clock, Receipt, ChartBar, Buildings, Funnel
} from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DateInput } from '../components/ui/DateInput';

export default function BillingReportsPage() {
  const { apiCall } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadReport(); }, []);

  async function loadReport() {
    setLoading(true);
    try {
      let url = '/billing/reports';
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await apiCall('get', url);
      setReport(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function filteredTransactions() {
    if (!report?.transactions) return [];
    if (statusFilter === 'all') return report.transactions;
    return report.transactions.filter(t => t.payment_status === statusFilter);
  }

  function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '-'; }
  function fmtDateTime(d) { return d ? new Date(d).toLocaleDateString('en-GB') + ' ' + new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'; }

  function generatePDF() {
    if (!report) return;
    const doc = new jsPDF();
    const txs = filteredTransactions();

    // Header
    doc.setFontSize(20);
    doc.setTextColor(115, 103, 240);
    doc.text('RoofMate', 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Billing Report', 14, 30);
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    const dateRange = startDate || endDate ? `${startDate || 'Start'} to ${endDate || 'Present'}` : 'All Time';
    doc.text(`Period: ${dateRange}  |  Generated: ${new Date().toLocaleDateString('en-GB')}`, 14, 37);

    // Summary boxes
    doc.setFillColor(245, 243, 255);
    doc.roundedRect(14, 43, 55, 22, 2, 2, 'F');
    doc.roundedRect(75, 43, 55, 22, 2, 2, 'F');
    doc.roundedRect(136, 43, 55, 22, 2, 2, 'F');

    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text('Total Revenue', 18, 50);
    doc.text('Paid Transactions', 79, 50);
    doc.text('Pending Amount', 140, 50);

    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text(`$${report.summary.total_revenue.toFixed(2)}`, 18, 60);
    doc.text(`${report.summary.paid_count}`, 79, 60);
    doc.text(`$${report.summary.total_pending.toFixed(2)}`, 140, 60);

    let y = 73;

    // Plan Breakdown
    if (report.plan_breakdown?.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 40);
      doc.text('Revenue by Plan', 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [['Plan', 'Transactions', 'Revenue']],
        body: report.plan_breakdown.map(p => [p.plan.charAt(0).toUpperCase() + p.plan.slice(1), p.count.toString(), `$${p.revenue.toFixed(2)}`]),
        theme: 'grid',
        headStyles: { fillColor: [115, 103, 240], fontSize: 8, cellPadding: 3 },
        bodyStyles: { fontSize: 8, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // Tenant Breakdown
    if (report.tenant_breakdown?.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 40);
      doc.text('Revenue by Tenant', 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [['Tenant', 'Transactions', 'Revenue']],
        body: report.tenant_breakdown.map(t => [t.tenant, t.count.toString(), `$${t.revenue.toFixed(2)}`]),
        theme: 'grid',
        headStyles: { fillColor: [115, 103, 240], fontSize: 8, cellPadding: 3 },
        bodyStyles: { fontSize: 8, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // Transaction Details
    if (txs.length > 0) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 40);
      doc.text('Transaction Details', 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [['Date', 'Tenant', 'Plan', 'Amount', 'Status', 'By']],
        body: txs.map(t => [
          fmtDate(t.created_at),
          t.tenant_name || t.metadata?.tenant_name || '-',
          (t.plan_id || '-').charAt(0).toUpperCase() + (t.plan_id || '-').slice(1),
          `$${(t.amount || 0).toFixed(2)}`,
          t.payment_status || '-',
          t.created_by || t.metadata?.admin_email || '-',
        ]),
        theme: 'grid',
        headStyles: { fillColor: [115, 103, 240], fontSize: 7, cellPadding: 2.5 },
        bodyStyles: { fontSize: 7, cellPadding: 2.5 },
        margin: { left: 14, right: 14 },
        columnStyles: { 0: { cellWidth: 22 }, 4: { cellWidth: 18 } },
      });
    }

    // Footer
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(`RoofMate Billing Report  |  Page ${i} of ${pages}`, 14, doc.internal.pageSize.height - 10);
    }

    doc.save(`roofmate-billing-report-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const txs = filteredTransactions();

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="billing-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileText size={22} className="text-primary" weight="duotone" />
          </div>
          <div>
            <h1 className="text-xl font-semibold" data-testid="reports-heading">Billing Reports</h1>
            <p className="text-sm text-muted-foreground">{report?.summary?.total_transactions || 0} total transactions</p>
          </div>
        </div>
        <Button onClick={generatePDF} className="rounded-md font-medium" data-testid="download-pdf-btn">
          <DownloadSimple size={16} className="mr-2" weight="bold" /> Download PDF
        </Button>
      </div>

      {/* Filters */}
      <Card className="shadow-card rounded-lg border-0">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarBlank size={16} className="text-muted-foreground" />
              <DateInput value={startDate} onChange={setStartDate} testId="filter-start" placeholder="Start date" />
              <span className="text-xs text-muted-foreground">to</span>
              <DateInput value={endDate} onChange={setEndDate} testId="filter-end" placeholder="End date" />
            </div>
            <div className="flex items-center gap-2">
              <Funnel size={16} className="text-muted-foreground" />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 text-sm rounded-md border border-input bg-background px-3" data-testid="filter-status">
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <Button onClick={loadReport} variant="outline" size="sm" className="rounded-md h-9" data-testid="apply-filters-btn">Apply</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {report?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Revenue', value: `$${report.summary.total_revenue.toFixed(2)}`, icon: CurrencyDollar, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
            { label: 'Paid', value: report.summary.paid_count, icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
            { label: 'Pending', value: `$${report.summary.total_pending.toFixed(2)}`, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
            { label: 'Total', value: report.summary.total_transactions, icon: Receipt, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
          ].map((s) => (
            <Card key={s.label} className="shadow-card rounded-lg border-0">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.bg}`}>
                  <s.icon size={20} className={s.color} weight="duotone" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
                  <p className="text-lg font-bold">{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Plan Breakdown */}
        {report?.plan_breakdown?.length > 0 && (
          <Card className="shadow-card rounded-lg border-0">
            <CardContent className="p-0">
              <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
                <ChartBar size={16} className="text-primary" weight="bold" />
                <h3 className="text-sm font-bold">Revenue by Plan</h3>
              </div>
              <Table>
                <TableHeader><TableRow className="bg-secondary/50"><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Plan</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Count</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase text-right">Revenue</TableHead></TableRow></TableHeader>
                <TableBody>
                  {report.plan_breakdown.map((p) => (
                    <TableRow key={p.plan}>
                      <TableCell><Badge variant="outline" className="capitalize text-xs">{p.plan}</Badge></TableCell>
                      <TableCell className="text-sm">{p.count}</TableCell>
                      <TableCell className="text-sm font-semibold text-right">${p.revenue.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Tenant Breakdown */}
        {report?.tenant_breakdown?.length > 0 && (
          <Card className="shadow-card rounded-lg border-0">
            <CardContent className="p-0">
              <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
                <Buildings size={16} className="text-primary" weight="bold" />
                <h3 className="text-sm font-bold">Revenue by Tenant</h3>
              </div>
              <Table>
                <TableHeader><TableRow className="bg-secondary/50"><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Tenant</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Count</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase text-right">Revenue</TableHead></TableRow></TableHeader>
                <TableBody>
                  {report.tenant_breakdown.map((t) => (
                    <TableRow key={t.tenant}>
                      <TableCell className="text-sm font-medium">{t.tenant}</TableCell>
                      <TableCell className="text-sm">{t.count}</TableCell>
                      <TableCell className="text-sm font-semibold text-right">${t.revenue.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Transaction Details */}
      <Card className="shadow-card rounded-lg border-0 overflow-hidden">
        <CardContent className="p-0">
          <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-2"><Receipt size={16} /> All Transactions ({txs.length})</h3>
          </div>
          {txs.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Receipt size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No transactions found</p>
              <p className="text-sm mt-1">Adjust filters or date range.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Tenant</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Plan</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Amount</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Initiated By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txs.map((tx, i) => (
                  <TableRow key={tx.id || i} className="hover:bg-secondary/30" data-testid={`tx-row-${i}`}>
                    <TableCell className="text-sm">{fmtDateTime(tx.created_at)}</TableCell>
                    <TableCell className="text-sm font-medium">{tx.tenant_name || tx.metadata?.tenant_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{tx.plan_id}</Badge></TableCell>
                    <TableCell className="text-sm font-semibold">${(tx.amount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {tx.payment_status === 'paid' ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"><CheckCircle size={11} className="mr-1" weight="fill" /> Paid</Badge>
                        : tx.payment_status === 'expired' ? <Badge variant="destructive" className="text-xs">Expired</Badge>
                        : <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs"><Clock size={11} className="mr-1" /> Pending</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{tx.created_by || tx.metadata?.admin_email || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
